import {
  DataTableColumnMenuButton,
  type DataTableColumn,
  type DataTableColumnVisibility,
  type DataTableSource,
  type TableRow,
} from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { PaginationFooter, ResourceBrowserLayout } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { DatasetResourceView, datasetResourceCardCellWidth } from "./DatasetResourceView";
import type { DatasetViewMode } from "./DatasetViewModeToggle";
import { tableStripError } from "@/components/table/tableStateMessages";

export type DatasetPageContentProps<Row extends TableRow> = {
  source: DataTableSource<Row>;
  columns: readonly DataTableColumn<Row>[];
  renderCard: (row: Row) => ReactNode;
  viewMode: DatasetViewMode;
  summaryLabel: ReactNode;
  layoutAriaLabel: string;
  viewAriaLabel: string;
  emptyMessage: ReactNode;
  loadingMessage: ReactNode;
  minWidth: string;
  cellWidth?: string;
  actionsColumnWidth?: string;
  showCheckbox?: boolean;
  // 선택 액션(선택). 목록 상단에 늘 표시하는 BulkActionBar 스트립 안에 놓는다.
  // 카드 뷰와 표 뷰가 같은 바를 공유한다. idle 상태에서는 검색 결과 문구를 표시하고,
  // 선택 상태에서는 개수와 해제(✕)를 바에 함께 표시한다.
  // 이 슬롯은 액션 버튼만 제공한다. 활성 규칙은 액션이 소유한다.
  selectionActions?: ReactNode;
  // 바 우측 슬롯(선택). 선택 여부와 관계없이 우측 끝에 남는 컨트롤(뷰 토글, 새로고침)을 놓는다.
  // 바 높이 계약이 sm 컨트롤 기준이므로 sm 컨트롤만 넣는다.
  barRightContent?: ReactNode;
  // 페이지 이동 footer 의 좌측 슬롯(선택). 선택과 관계없이 계속 보여야 하는 요소(통합 진행 칩)를
  // 놓는다. paginated=false 이면 footer 가 없으므로 이 슬롯도 표시하지 않는다.
  footerLeading?: ReactNode;
  // 테이블 컬럼 가시성 메뉴(선택). 둘 다 주면 table 뷰 페이지네이션 우측에 컬럼 메뉴를 표시한다.
  columnVisibility?: DataTableColumnVisibility;
  onColumnVisibilityChange?: (visibility: DataTableColumnVisibility) => void;
  // 슬롯. notice 와 error 는 상단 안내로 표시한다.
  notice?: ReactNode;
  error?: ReactNode;
  // paginated=false 이면 페이저를 생략한다(페이지가 하나뿐인 소스).
  // prev/next 를 주지 않으면 source.goToPage 로 처리한다.
  // URL page 동기화가 필요한 페이지는 핸들러를 주입한다.
  paginated?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
};

// 데이터셋 목록 콘텐츠 셸. 다음 요소를 가진다:
//   - 카드/테이블 리소스 뷰
//   - 상단 BulkActionBar 스트립
//   - 페이지네이션과 선택적 컬럼 메뉴
// 페이지별 차이(소스, 컬럼, 카드, 페이징 방식, 부가 슬롯)는 prop 으로 받는다.
// 검색과 필터는 페이지(belowTabs 필터 블록)가 소유한다. 뷰 토글과 새로고침은 바 우측
// 슬롯(barRightContent)이 소유한다. 그래서 이 셸에는 별도 툴바가 없다(summaryInFilter 고정).
// 결과 수는 목록 상단 스트립이 소유한다. 로딩, 페이지 상태, 에러는 source 스냅샷에서 읽는다.
export function DatasetPageContent<Row extends TableRow>({
  source,
  columns,
  renderCard,
  viewMode,
  summaryLabel,
  layoutAriaLabel,
  viewAriaLabel,
  emptyMessage,
  loadingMessage,
  minWidth,
  cellWidth = datasetResourceCardCellWidth,
  actionsColumnWidth,
  showCheckbox = false,
  selectionActions = null,
  barRightContent = null,
  footerLeading = null,
  columnVisibility,
  onColumnVisibilityChange,
  notice = null,
  error,
  paginated = true,
  onPreviousPage,
  onNextPage,
}: DatasetPageContentProps<Row>): ReactNode {
  const snapshot = source.getSnapshot();
  const { loadState, page, size, total } = snapshot;
  // 행이 없는 조회 실패는 목록 본문이 알린다. 띠는 행이 남아 있는 실패와 페이지가 넘긴 동작 오류를 알린다.
  const listError = tableStripError(snapshot);
  const isLoading = loadState === "loading";
  const totalPages = Math.max(1, Math.ceil(total / size));

  const columnMenu =
    viewMode === "table" && columnVisibility && onColumnVisibilityChange ? (
      <DataTableColumnMenuButton
        columns={columns}
        columnVisibility={columnVisibility}
        menuPlacement="top"
        onColumnVisibilityChange={onColumnVisibilityChange}
      />
    ) : null;

  const pager = paginated ? (
    <PaginationFooter
      page={page}
      size={size}
      total={total}
      isLoading={isLoading}
      ariaLabel="Dataset 페이지 이동"
      leadingAction={footerLeading}
      trailingAction={columnMenu}
      onPrevious={onPreviousPage ?? (() => void source.goToPage(Math.max(1, page - 1)))}
      onNext={onNextPage ?? (() => void source.goToPage(Math.min(totalPages, page + 1)))}
    />
  ) : null;

  return (
    <>
      <ResourceBrowserLayout
        ariaLabel={layoutAriaLabel}
        summaryCount={total}
        summaryLabel={summaryLabel}
        notice={notice}
        error={error ?? listError}
        // 목록 조회 실패일 때만 다시 받는다. 페이지가 넘긴 오류(동작 실패)는 다시 받아도 사라지지 않는다.
        onRetry={error == null && listError != null ? () => void source.refresh() : undefined}
        summaryInFilter
        footer={pager}
      >
        {/* 목록 상단 스트립. idle 상태에서는 검색 결과를 표시한다. 검색 결과는 레일이 아니라
            이 스트립이 소유한다. 선택 상태에서는 개수, 액션, 해제를 표시한다.
            래퍼는 dataView 의 grid/min-height 체인을 그대로 통과시킨다. */}
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            actions={selectionActions ?? undefined}
            rightContent={barRightContent ?? undefined}
            idleContent={
              /* 결과 수는 공용 FilterResultCount 로 표시한다. 학습 세션 도구 띠와 타이포·색이 같다. */
              <FilterResultCount count={total} unitLabel={summaryLabel} />
            }
            ariaLabel="Dataset 목록 도구"
          />
          <DatasetResourceView
            source={source}
            columns={columns}
            columnVisibility={columnVisibility}
            emptyMessage={emptyMessage}
            loadingMessage={loadingMessage}
            ariaLabel={viewAriaLabel}
            cellWidth={cellWidth}
            viewMode={viewMode}
            minWidth={minWidth}
            actionsColumnWidth={actionsColumnWidth}
            showCheckbox={showCheckbox}
            renderCard={renderCard}
          />
        </div>
      </ResourceBrowserLayout>
    </>
  );
}
